/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import ProductCarouselComponent from "@eisinc/App/Components/ProductCarouselComponent/ProductCarouselComponent";
import { CategoryDetailsPageContext } from "@eisinc/Overrides/Pages/CategoryDetailsPage";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BrandDetailsPageContext } from "@insite/content-library/Pages/BrandDetailsPage";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    carouselTemplat = "carouselTemplat",
    carouselType = "carouselType",
    maxNumberOfColumns = "maxNumberOfColumns",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselTemplat]: string;
        [fields.carouselType]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    // products: getProductsForProductInfoList(state, ownProps.id),
    state,
    pageType: getCurrentPage(state).type,
});

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

const FeaturedProductCarousel: React.FC<Props> = ({
    id,
    theme,
    fields,
    loadCarouselProducts,
    pageType,
    shellContext,
    state,
}) => {
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = React.useContext(BrandStateContext).value;
    const category = React.useContext(CategoryContext);
    const productContext = React.useContext(ProductContext);
    const complixId = id + (productContext?.product?.id || "") + (category?.id || "") + (brand?.id || "");
    const products = getProductsForProductInfoList(state, complixId);

    React.useEffect(() => {
        if (fields.carouselType) {
            const shouldLoad = fields.carouselType && (productContext?.product?.id || category?.id || brand?.id);
            shouldLoad &&
                loadCarouselProducts({
                    carouselId: complixId,
                    carouselType: fields.carouselType,
                    relatedProductType: "",
                    seedWithManuallyAssigned: "",
                    displayProductsFrom: "",
                    selectedCategoryIds: [],
                    numberOfProductsToDisplay: 50,
                    isProductDetailsPage,
                    productId: productContext?.product?.id,
                    isProductListPage,
                    category,
                    isBrandDetailsPage,
                    brand,
                });
        }
    }, [fields.carouselType, productContext?.product?.id, category?.id, brand?.id]);

    if (!products || products.length === 0) {
        return null;
    }

    const customFields = {
        ...fields,
        relatedProductType: "",
        seedWithManuallyAssigned: "",
        displayProductsFrom: "",
        selectedCategoryIds: [],
        numberOfProductsToDisplay: 50,
    };

    return (
        <ProductCarouselComponent
            fields={customFields}
            isBrandDetailsPage={isBrandDetailsPage}
            isProductDetailsPage={isProductDetailsPage}
            isProductListPage={isProductListPage}
            products={products}
            id={complixId}
            brand={brand}
            carouselName="featuredProductsCarousel"
        />
    );
};

const carouselTypeOptions = [
    {
        value: "featuredProducts",
        displayName: "Featured Category/Brand Products",
    },
];

const carouselTemplatOptions = [
    { value: "carousel", displayName: "Carousel" },
    { value: "2*3", displayName: "2 * 3" },
    { value: "1*6", displayName: "1 * 6" },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(FeaturedProductCarousel))),
    definition: {
        group: "Common",
        icon: "Carousel",
        allowedContexts: [CategoryDetailsPageContext, BrandDetailsPageContext, ProductListPageContext],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.carouselTemplat,
                displayName: "Carousel Template",
                editorTemplate: "DropDownField",
                options: carouselTemplatOptions,
                defaultValue: "carousel",
                fieldType: "General",
                sortOrder: 1,
                isRequired: true,
            },
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "featuredProducts",
                fieldType: "General",
                sortOrder: 2,
                isRequired: true,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 4,
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 8,
                isVisible: item => item.fields.carouselTemplat === "carousel",
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 12,
                isVisible: item => item.fields.carouselTemplat === "carousel",
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 13,
                isVisible: item => item.fields.carouselTemplat === "carousel",
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 14,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add To Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 15,
                isVisible: item => item.fields.carouselTemplat === "carousel" || item.fields.carouselTemplat === "2*3",
            },
            {
                name: fields.showAddToList,
                displayName: "Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 16,
                isVisible: item => item.fields.carouselTemplat === "carousel" || item.fields.carouselTemplat === "2*3",
            },
        ],
    },
};

export default widgetModule;
